<template>
  <header
    ref="headerRef"
    class="raw-glass--md fixed inset-0 bottom-auto z-10 grid grid-cols-[auto_auto_1fr] items-center gap-12 px-[--raw-edge-space] py-10 max-md:pb-0 md:grid-cols-[1fr_auto_1fr]"
  >
    <!-- Back Market storefront: favicon on mobile, wordmark on desktop. -->
    <a
      :href="i18n(backMarket.url)"
      target="_blank"
      rel="noopener"
      :aria-label="i18n(backMarket.label)"
      class="justify-self-start"
      :class="{ 'invisible hidden md:block': isWebview }"
      @click="handleBackMarket"
    >
      <RevIllustration
        src="/mine-my-phone/back-market_favicon.svg"
        alt=""
        :width="44"
        :height="44"
        class="md:hidden"
      />
      <RevIllustration
        src="/mine-my-phone/back-market_logo.svg"
        alt=""
        :width="134"
        :height="16"
        class="hidden h-16 md:block"
      />
    </a>

    <!-- Raw Logo: favicon on mobile, localized wordmark on desktop. -->
    <button type="button" @click="handleRestart">
      <RevIllustration
        src="/mine-my-phone/raw_favicon.svg"
        alt=""
        :width="44"
        :height="44"
        class="h-44 md:hidden"
      />
      <RevIllustration
        :src="rawLogo.src"
        alt=""
        :width="rawLogo.width"
        :height="36"
        class="hidden !h-44 w-auto md:block"
      />
    </button>

    <!-- Nav -->
    <nav class="flex gap-6 justify-self-end">
      <!-- Metals Trigger -->
      <button
        data-glass="glow"
        class="raw-glass--max-md rounded-full flex aspect-square h-44 items-center justify-center border transition-colors duration-[var(--raw-fast)] ease-[var(--raw-ease-out-quart)] active:border-[white] active:bg-[white] md:border-[--raw-verylightgrey] md:bg-[--raw-verylightgrey] md:hover:border-[white] md:hover:bg-[white]"
        @click="handleNavigate(MODAL_ROUTES.METAL_LIBRARY)"
      >
        <PickaxeIcon />
      </button>

      <!-- Content Trigger -->
      <button
        data-glass="glow"
        class="raw-glass--max-md rounded-full flex aspect-square h-44 items-center justify-center border transition-colors duration-[var(--raw-fast)] ease-[var(--raw-ease-out-quart)] active:border-[white] active:bg-[white] md:border-[--raw-verylightgrey] md:bg-[--raw-verylightgrey] md:hover:border-[white] md:hover:bg-[white]"
        @click="handleNavigate(MODAL_ROUTES.CONTENT_GALLERY)"
      >
        <VideoIcon />
      </button>

      <TakeAction />
    </nav>
  </header>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useIsWebview } from '@backmarket/nuxt-module-navigation/useIsWebview'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevIllustration } from '@ds/components/Illustration'

import TakeAction from '~/scopes/campaign/components/takeAction/TakeActionButton.vue'
import { useElementHeightVar } from '~/scopes/campaign/composables/useElementHeightVar'
import { useIntroState } from '~/scopes/campaign/composables/useIntroState'
import {
  type ModalState,
  useModalState,
} from '~/scopes/campaign/composables/useModalState'
import backMarket from '~/scopes/campaign/const/back-market.translations'
import { MODAL_ROUTES } from '~/scopes/campaign/const/config.const'
import PickaxeIcon from '~/scopes/campaign/icons/PickaxeIcon.vue'
import VideoIcon from '~/scopes/campaign/icons/VideoIcon.vue'

const { trackClick } = useTracking()
const i18n = useI18n()
const route = useRoute()
const { activeModal, navigate, closeToImpacts, navigateToHome } =
  useModalState()
const { resetIntro } = useIntroState()

// Intrinsic file size; the `!` in `!h-44` beats the `h-auto` the DS appends.
const RAW_LOGOS = {
  'en-us': { src: '/mine-my-phone/raw_logo--en-us.svg', width: 191 },
  'de-de': { src: '/mine-my-phone/raw_logo--de-de.svg', width: 248 },
  'es-es': { src: '/mine-my-phone/raw_logo--es-es.svg', width: 180 },
} as const

// Route param, not `useI18nLocale()`: the header stays mounted across a switch.
const rawLogo = computed(() => {
  const param = route.params.locale
  const locale = Array.isArray(param) ? param[0] : param

  // Only three markets have artwork, and a 404 would be a broken image in a
  // permanently visible header.
  return RAW_LOGOS[locale as keyof typeof RAW_LOGOS] ?? RAW_LOGOS['en-us']
})

const headerRef = ref<HTMLElement | null>(null)
useElementHeightVar(headerRef, '--raw-header-height')
const isWebview = useIsWebview()

function handleNavigate(state: ModalState) {
  trackClick({ zone: 'header-nav', name: state })
  if (activeModal.value === state) {
    // Toggling an overlay off returns to the impact the user came from (or
    // home) instead of forgetting it.
    closeToImpacts()
  } else {
    navigate(state)
  }
}

// The Back Market logo/favicon leave the campaign for the main storefront.
function handleBackMarket() {
  trackClick({ zone: 'header', name: 'backmarket-home' })
}

// The campaign logo restarts the campaign: re-open the intro and revert the URL
// to bare home. Dismissing the intro then lands the user in the default state —
// BGMap flies back to the world once `showIntro` clears on a bare-home route —
// so a deep link or any in-app route is wiped back to the clean starting point.
function handleRestart() {
  trackClick({ zone: 'header', name: 'restart' })
  navigateToHome()
  resetIntro()
}
</script>
